import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Typography } from "@mui/material";
import { ROUTE_VIDEO_COMPLETE } from "../../constants/routes";
import { setErrorMessage, updateUserVideos, getPreSignedUrl, getUserVideos } from "../../redux/actions/appActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppLoader from "../common/AppLoader";

interface VideoReplaceProps {
  questionId: number;
}

const VideoReplace: React.FC<VideoReplaceProps> = ({ questionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = (event: React.MouseEvent) => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async(event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Allowed video MIME types
    const allowedMimeTypes = ['video/mp4', 'video/webm', 'video/x-msvideo', 'video/quicktime', 'video/x-matroska'];
    const allowedExtensions = ['.mp4', '.webm', '.avi', '.mov', '.mkv'];
    
    // Validate MIME type
    if (!allowedMimeTypes.includes(file.type)) {
      dispatch(setErrorMessage("Invalid file type. Please upload a video file (MP4, WebM, AVI, MOV, MKV)"));
      event.target.value = '';
      return;
    }
    
    // Validate file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExtension)) {
      dispatch(setErrorMessage("Invalid file extension. Please upload a video file (MP4, WebM, AVI, MOV, MKV)"));
      event.target.value = '';
      return;
    }
    
    try {
      const preSignedUrl = await getPreSignedUrl(file.name);
      if (!preSignedUrl) {
        dispatch(setErrorMessage("Error getting pre-signed URL"));
        return;
      }

      setIsLoading(true);

      await fetch(preSignedUrl, {
        method: "PUT",
        body: file,
      });

      const fileUrl = preSignedUrl.split("?")[0]; 

      dispatch(updateUserVideos([{
        questionId: questionId,
        videoPath: fileUrl,
        videoPrivacy: "PUBLIC",
      }], onSuccess, onError));
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setErrorMessage("Error uploading file"));
      setIsLoading(false);
    }
  };

  const onSuccess = () => {
    dispatch(getUserVideos());
    setIsLoading(false);
    navigate(`${ROUTE_VIDEO_COMPLETE}${'?edit'}`);
  };

  const onError = () => {
    setIsLoading(false);
    dispatch(setErrorMessage("Error uploading file"));
  };
  
  return (
    <>
      {isLoading && ReactDOM.createPortal(
        <AppLoader text="Uploading video..." />,
        document.body
      )}
      <input
        hidden
        type="file"
        ref={fileInputRef}
        capture={false}
        onChange={handleFileChange}
        accept=".mp4,.webm,.avi,.mov,.mkv,video/mp4,video/webm,video/x-msvideo,video/quicktime,video/x-matroska"
      />
      <Typography
        onClick={handleClick}
        style={{ 
          color: "#756DE9", 
          fontSize: "14px", 
          fontWeight: "550", 
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          justifyContent: "center"
        }}
      >
        <img 
          src="/images/icons/checkbox-multiple-line.png" 
          alt="Replace" 
          style={{ width: "18px", height: "18px" }}
        />
        Replace
      </Typography>
    </>
  );
};

export default VideoReplace;

